import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, ExternalLink, Link as LinkIcon, Edit } from 'lucide-react';

const RecipeManagement = () => {
  const [selectedFase, setSelectedFase] = useState('0');
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [newLink, setNewLink] = useState({
    fase: '0',
    titulo: '',
    url: '',
    orden: 0
  });
  const [editForm, setEditForm] = useState({
    titulo: '',
    url: '',
    orden: 0
  });

  useEffect(() => {
    loadLinks();
  }, [selectedFase]);

  const loadLinks = async () => {
    setLoading(true);
    try {
      const api = createAuthAPI();
      const response = await api.get(`/admin/recipe-links?fase=${selectedFase}`);
      setLinks(response.data || []);
    } catch (error) {
      console.error('Error loading recipe links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      await api.post('/admin/recipe-links', {
        ...newLink,
        fase: selectedFase
      });
      toast.success('Link añadido exitosamente');
      setShowAddDialog(false);
      setNewLink({
        fase: selectedFase,
        titulo: '',
        url: '',
        orden: 0
      });
      loadLinks();
    } catch (error) {
      toast.error('Error al añadir link');
    }
  };

  const openEditDialog = (link) => {
    setEditingLink(link);
    setEditForm({
      titulo: link.titulo,
      url: link.url,
      orden: link.orden
    });
    setShowEditDialog(true);
  };

  const handleUpdateLink = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      await api.put(`/admin/recipe-links/${editingLink.id}`, editForm);
      toast.success('Link actualizado exitosamente');
      setShowEditDialog(false);
      setEditingLink(null);
      loadLinks();
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error('Error al actualizar link');
    }
  };


  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('¿Estás seguro de eliminar este link?')) return;
    
    try {
      const api = createAuthAPI();
      console.log('Deleting link ID:', linkId);
      const response = await api.delete(`/admin/recipe-links/${linkId}`);
      console.log('Delete response:', response);
      toast.success('Link eliminado correctamente');
      await loadLinks(); // Reload links after delete
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error(`Error al eliminar link: ${error.response?.data?.detail || error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="pharmacy-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-green-800">Gestión de Links de Recetas</CardTitle>
            <Button
              onClick={loadLinks}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Fase Selector */}
          <div className="space-y-4">
            <Label className="text-base font-semibold text-gray-700">Selecciona la Fase:</Label>
            <Select value={selectedFase} onValueChange={setSelectedFase}>
              <SelectTrigger className="text-base py-6 border-2 border-gray-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="0">Fase 0 - Inicio (primeros 7 días)</SelectItem>
                <SelectItem value="1a">Fase 1a - + Aves</SelectItem>
                <SelectItem value="1b">Fase 1b - + Pescado</SelectItem>
                <SelectItem value="1c">Fase 1c - + Atún/Salmón</SelectItem>
                <SelectItem value="2a">Fase 2a - + Yogures/Claras</SelectItem>
                <SelectItem value="2b">Fase 2b - + Aguacate/Huevos/Cerdo</SelectItem>
                <SelectItem value="3">Fase 3 - Estabilización</SelectItem>
                <SelectItem value="4">Fase 4 - Mantenimiento OMS</SelectItem>
                <SelectItem value="5">Fase 5 - Detox 10 días Forte pharma</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Add Link Button */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="pharmacy-button text-white w-full flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" />
                Añadir Link para Fase {selectedFase}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white shadow-2xl">
              <DialogHeader className="bg-green-50 -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
                <DialogTitle className="text-2xl font-bold text-green-800">
                  Nuevo Link - Fase {selectedFase}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddLink} className="space-y-4 mt-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-gray-700">Título *</Label>
                  <Input
                    value={newLink.titulo}
                    onChange={(e) => setNewLink({...newLink, titulo: e.target.value})}
                    required
                    className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                    placeholder="Ej: Ensalada de Espinacas y Brócoli"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-gray-700">URL (YouTube, TikTok, Google, etc.) *</Label>
                  <Input
                    type="url"
                    value={newLink.url}
                    onChange={(e) => setNewLink({...newLink, url: e.target.value})}
                    required
                    className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-gray-700">Orden (opcional)</Label>
                  <Input
                    type="number"
                    value={newLink.orden}
                    onChange={(e) => setNewLink({...newLink, orden: parseInt(e.target.value) || 0})}
                    className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                    placeholder="0"
                  />
                </div>
                <Button type="submit" className="w-full pharmacy-button text-white py-6 text-lg font-semibold">
                  ✓ Añadir Link
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Links List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700">
                Links de Fase {selectedFase} ({links.length})
              </h3>
            </div>

            {loading ? (
              <p className="text-center text-gray-500 py-8">Cargando links...</p>
            ) : links.length > 0 ? (
              <div className="space-y-3">
                {links.map((link) => (
                  <Card key={link.id} className="pharmacy-card">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <LinkIcon className="h-4 w-4 text-green-600" />
                            <h4 className="font-semibold text-green-800">{link.titulo}</h4>
                          </div>
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 break-all"
                          >
                            {link.url}
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                          <p className="text-xs text-gray-500 mt-1">Orden: {link.orden}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            onClick={() => openEditDialog(link)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Editar
                          </Button>
                          <Button
                            onClick={() => handleDeleteLink(link.id)}
                            variant="destructive"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <LinkIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No hay links para Fase {selectedFase}</p>
                <p className="text-sm text-gray-500">Añade tu primer link de YouTube, TikTok o cualquier plataforma</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {showEditDialog && editingLink && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md bg-white shadow-2xl">
            <DialogHeader className="bg-green-50 -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
              <DialogTitle className="text-xl font-bold text-green-800">
                Editar Link de Receta
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateLink} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-titulo" className="text-base font-semibold">Título</Label>
                <Input
                  id="edit-titulo"
                  value={editForm.titulo}
                  onChange={(e) => setEditForm({...editForm, titulo: e.target.value})}
                  placeholder="Ej: Ensalada de quinoa"
                  required
                  className="text-base py-6 border-2"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-url" className="text-base font-semibold">URL (YouTube, TikTok, Instagram...)</Label>
                <Input
                  id="edit-url"
                  type="url"
                  value={editForm.url}
                  onChange={(e) => setEditForm({...editForm, url: e.target.value})}
                  placeholder="https://..."
                  required
                  className="text-base py-6 border-2"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-orden" className="text-base font-semibold">Orden (número)</Label>
                <Input
                  id="edit-orden"
                  type="number"
                  value={editForm.orden}
                  onChange={(e) => setEditForm({...editForm, orden: parseInt(e.target.value)})}
                  className="text-base py-6 border-2"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingLink(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 pharmacy-button text-white"
                >
                  Actualizar Link
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default RecipeManagement;